import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { fetchMessageThreads, type MessageThread } from "../../../../packages/shared/src/messages.ts";
import { MessageNotice } from "./MessageNotice.tsx";
import { MessageThreadScreen, type MessageThreadOutbox } from "./MessageThreadScreen.tsx";
import { StartMessageScreen } from "./StartMessageScreen.tsx";
import { fetchMessageCrewSites } from "./message-crew-sites.ts";
import {
  BOARDS_NOTE,
  FIELD_INBOX_FILTER_LABELS,
  FIELD_INBOX_FILTERS,
  MESSAGES_EMPTY_BODY,
  MESSAGES_EMPTY_TITLE,
  MESSAGES_NOTE,
  formatMessageWhen,
  messageInitials,
  messageThreadSecondary,
  type FieldInboxFilter,
  type MessageCrewSite,
} from "./message-model.ts";
import { useMessageOutbox } from "./use-message-outbox.ts";

type InboxSurface = "list" | "start" | "thread";

export function FieldInbox({
  accountId,
  client,
  currentAuthorName,
  currentAuthorRole,
  officerId,
}: {
  readonly accountId: string;
  readonly client: SupabaseClient;
  readonly currentAuthorName: string;
  readonly currentAuthorRole: string;
  readonly officerId: string;
}) {
  const [filter, setFilter] = useState<FieldInboxFilter>("all");
  const [surface, setSurface] = useState<InboxSurface>("list");
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [threads, setThreads] = useState<readonly MessageThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [threadsFailure, setThreadsFailure] = useState<string | null>(null);
  const [crewSites, setCrewSites] = useState<readonly MessageCrewSite[]>([]);
  const [crewSitesFailure, setCrewSitesFailure] = useState<string | null>(null);
  const [threadRefreshVersion, setThreadRefreshVersion] = useState(0);
  const threadGenerationRef = useRef(0);
  const crewGenerationRef = useRef(0);

  const loadThreads = useCallback(async () => {
    const generation = ++threadGenerationRef.current;
    setLoading(true);
    setThreadsFailure(null);
    const load = await fetchMessageThreads(client, accountId);
    if (threadGenerationRef.current !== generation) return;
    setLoading(false);
    if (load.status !== "ready") {
      setThreads([]);
      setThreadsFailure(load.reason);
      return;
    }
    setThreads(load.value);
  }, [accountId, client]);

  const loadCrewSites = useCallback(async () => {
    const generation = ++crewGenerationRef.current;
    const load = await fetchMessageCrewSites(client, accountId);
    if (crewGenerationRef.current !== generation) return;
    if (load.status !== "ready") {
      setCrewSites([]);
      setCrewSitesFailure(load.reason);
      return;
    }
    setCrewSites(load.value);
    setCrewSitesFailure(null);
  }, [accountId, client]);

  const onSent = useCallback(() => {
    setThreadRefreshVersion((version) => version + 1);
    void loadThreads();
  }, [loadThreads]);

  const messageOutbox = useMessageOutbox({ accountId, client, officerId, onSent });
  const {
    networkOnline,
    outbox,
    queuePost,
    reconcile,
    retryPost,
  } = messageOutbox;
  const threadOutbox = useMemo<MessageThreadOutbox>(() => ({
    networkOnline,
    outbox,
    queuePost,
    reconcile,
    retryPost,
  }), [networkOnline, outbox, queuePost, reconcile, retryPost]);

  useEffect(() => {
    threadGenerationRef.current += 1;
    crewGenerationRef.current += 1;
    setFilter("all");
    setSurface("list");
    setSelectedThreadId(null);
    setThreads([]);
    setCrewSites([]);
    setThreadsFailure(null);
    setCrewSitesFailure(null);
    setThreadRefreshVersion(0);
  }, [accountId, officerId]);

  useEffect(() => {
    void loadThreads();
    void loadCrewSites();
  }, [loadCrewSites, loadThreads]);

  if (surface === "thread" && selectedThreadId !== null) {
    return (
      <MessageThreadScreen
        accountId={accountId}
        client={client}
        currentAuthorName={currentAuthorName}
        currentAuthorRole={currentAuthorRole}
        officerId={officerId}
        onBack={() => {
          setSelectedThreadId(null);
          setSurface("list");
          void loadThreads();
        }}
        onThreadRead={loadThreads}
        outbox={threadOutbox}
        refreshVersion={threadRefreshVersion}
        threadId={selectedThreadId}
      />
    );
  }

  if (surface === "start") {
    return (
      <StartMessageScreen
        accountId={accountId}
        client={client}
        crewSites={crewSites}
        crewSitesFailure={crewSitesFailure}
        onBack={() => setSurface("list")}
        onCreated={(threadId) => {
          setSelectedThreadId(threadId);
          setSurface("thread");
          void loadThreads();
        }}
      />
    );
  }

  const hasUnreadMessages = threads.some((thread) => thread.unreadPostCount > 0);
  const showMessages = filter === "all" || filter === "messages";

  return (
    <section className="field-inbox" aria-labelledby="field-inbox-title">
      <header className="field-inbox__header">
        <div>
          <p className="field-inbox__eyebrow">Field Inbox</p>
          <h2 id="field-inbox-title">Inbox</h2>
          <p>{currentAuthorName} · {currentAuthorRole}</p>
        </div>
        <button className="messages-quiet-button" type="button" disabled={loading} onClick={() => void loadThreads()}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      <div className="field-inbox__tabs" role="tablist" aria-label="Inbox filters">
        {FIELD_INBOX_FILTERS.map((key) => (
          <button
            aria-selected={filter === key}
            className="field-inbox__tab"
            key={key}
            role="tab"
            type="button"
            onClick={() => setFilter(key)}
          >
            {FIELD_INBOX_FILTER_LABELS[key]}
            {key === "messages" && hasUnreadMessages ? (
              <span className="field-inbox__dot" aria-label="Unread Messages" role="status" />
            ) : null}
          </button>
        ))}
      </div>

      {filter === "all" || filter === "critical" ? (
        <InboxStub
          label="Critical"
          copy="Critical is not connected in Guard Web Inbox yet. Use Guard mobile for active alerts."
        />
      ) : null}
      {filter === "all" || filter === "action" ? (
        <InboxStub
          label="Action required"
          copy="Action is not connected in Guard Web Inbox yet. Use Guard mobile for acknowledgements."
        />
      ) : null}
      {filter === "all" || filter === "boards" ? (
        <InboxStub label="Boards · announcements" copy={`Boards are not connected in Guard Web Inbox yet. ${BOARDS_NOTE}`} />
      ) : null}
      {showMessages ? (
        <MessagesSection
          loading={loading}
          onOpen={(threadId) => {
            setSelectedThreadId(threadId);
            setSurface("thread");
          }}
          onStart={() => setSurface("start")}
          threads={threads}
          threadsFailure={threadsFailure}
        />
      ) : null}
    </section>
  );
}

function InboxStub({ copy, label }: { readonly copy: string; readonly label: string }) {
  return (
    <section className="field-inbox__band" aria-label={label}>
      <h3>{label}</h3>
      <div className="field-inbox__stub"><p>{copy}</p></div>
    </section>
  );
}

function MessagesSection({
  loading,
  onOpen,
  onStart,
  threads,
  threadsFailure,
}: {
  readonly loading: boolean;
  readonly onOpen: (threadId: string) => void;
  readonly onStart: () => void;
  readonly threads: readonly MessageThread[];
  readonly threadsFailure: string | null;
}) {
  return (
    <section className="field-inbox__band" aria-label="Messages">
      <div className="field-inbox__band-heading">
        <div>
          <h3>Messages · direct &amp; crew</h3>
          <p>{MESSAGES_NOTE}</p>
        </div>
        <button className="messages-new" type="button" onClick={onStart}>New message</button>
      </div>
      {threadsFailure !== null ? <MessageNotice copy={threadsFailure} /> : null}
      {loading && threads.length === 0 ? <p className="messages-state" role="status">Loading Messages...</p> : null}
      {!loading && threadsFailure === null && threads.length === 0 ? (
        <div className="messages-empty">
          <h4>{MESSAGES_EMPTY_TITLE}</h4>
          <p>{MESSAGES_EMPTY_BODY}</p>
        </div>
      ) : null}
      <div className="messages-thread-list">
        {threads.map((thread) => (
          <button
            aria-label={`Open ${thread.name}`}
            className="messages-thread-row"
            key={thread.threadId}
            type="button"
            onClick={() => onOpen(thread.threadId)}
          >
            <span className="messages-avatar" aria-hidden="true">{messageInitials(thread.name)}</span>
            <span className="messages-thread-row__copy">
              <strong>{thread.name}</strong>
              <span>{messageThreadSecondary(thread)}</span>
            </span>
            <span className="messages-thread-row__meta">
              {thread.lastPostAt !== null ? <span>{formatMessageWhen(thread.lastPostAt)}</span> : null}
              {thread.unreadPostCount > 0 ? (
                <span className="field-inbox__dot" aria-label="Unread messages" role="status" />
              ) : null}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
